import { endpoints } from '~/api/endpoints';
import { integrationsSseBaseUrl } from '~/constants/config/env';
import { useAuthStore } from '~/store/auth.store';
import { useLocaleStore } from '~/store/locale.store';

import type { HistoryEvent, HistoryStreamControlFrame } from '../types/workflow-inspection.types';

/** Stream-level HTTP error (status + Retry-After), duck-typed on `.status` by the hook. */
export class WorkflowHistoryStreamError extends Error {
  readonly status: number;
  readonly retryAfterSeconds?: number;
  constructor(status: number, message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = 'WorkflowHistoryStreamError';
    this.status = status;
    if (retryAfterSeconds !== undefined) this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** A parsed frame: a new history event, or a heartbeat/degraded/closed control frame. */
export type HistoryStreamFrame =
  | { kind: 'event'; event: HistoryEvent }
  | { kind: 'control'; frame: HistoryStreamControlFrame };

export interface WorkflowHistoryStreamOptions {
  workflowId: string;
  runId?: string | null;
  /** Last eventId already held — the server sends only newer events. */
  afterEventId?: number | null;
  onOpen?: () => void;
  onFrame: (frame: HistoryStreamFrame) => void;
  /** Network / HTTP failure before or during streaming. Called once. */
  onError: (error: Error) => void;
  /** The server ended the response (normally right after a `closed` frame). */
  onComplete: () => void;
}

/** Retry-After as seconds (delta-seconds or HTTP-date); undefined when absent/malformed. */
function parseRetryAfter(headerValue: string | null): number | undefined {
  if (!headerValue) return undefined;
  const trimmed = headerValue.trim();
  if (/^\d+$/.test(trimmed)) return Number.parseInt(trimmed, 10);
  const dateMs = Date.parse(trimmed);
  if (Number.isNaN(dateMs)) return undefined;
  return Math.max(0, Math.ceil((dateMs - Date.now()) / 1000));
}

/**
 * Opens the live history tail (`GET /workflows/{id}/history/stream`, BE §3.2).
 * Raw `fetch` (not `EventSource`) so auth + `X-Subsidiary-ID` headers can be
 * sent and the body isn't buffered; manual `\n\n` framing. Returns the
 * `AbortController` used for teardown.
 */
export function createWorkflowHistoryStream(
  options: WorkflowHistoryStreamOptions,
): AbortController {
  const { workflowId, runId, afterEventId, onOpen, onFrame, onError, onComplete } = options;
  const controller = new AbortController();

  const params = new URLSearchParams();
  if (runId) params.set('runId', runId);
  if (afterEventId !== undefined && afterEventId !== null)
    params.set('afterEventId', String(afterEventId));
  const query = params.toString();
  const fullUrl = `${integrationsSseBaseUrl}/${endpoints.workflows.historyStream(workflowId)}${query ? `?${query}` : ''}`;

  const { user, accessToken } = useAuthStore.getState();
  const tenantId = user?.activeTenantId;
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Accept-Language': useLocaleStore.getState().activeLocale,
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (tenantId) headers['X-Subsidiary-ID'] = tenantId;

  void (async () => {
    try {
      const response = await fetch(fullUrl, { method: 'GET', headers, signal: controller.signal });
      if (!response.ok) {
        throw new WorkflowHistoryStreamError(
          response.status,
          `Workflow history stream failed: ${String(response.status)} ${response.statusText}`,
          parseRetryAfter(response.headers.get('Retry-After')),
        );
      }
      if (!response.body) throw new Error('Response body is null — streaming not supported');
      onOpen?.();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) processBlock(buffer, onFrame);
          onComplete();
          return;
        }
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';
        for (const part of parts) if (part.trim()) processBlock(part, onFrame);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  })();

  return controller;
}

/** Parse one SSE block into a typed frame; unknown frame names / bad JSON are dropped. */
function processBlock(block: string, onFrame: (frame: HistoryStreamFrame) => void): void {
  let eventName = 'message';
  let dataStr = '';
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) eventName = line.slice(6).trim();
    else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
  }
  if (!dataStr) return;
  let parsed: unknown;
  try {
    parsed = JSON.parse(dataStr);
  } catch {
    return;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return;

  if (eventName === 'event') {
    onFrame({ kind: 'event', event: parsed as HistoryEvent });
  } else if (eventName === 'heartbeat' || eventName === 'degraded' || eventName === 'closed') {
    onFrame({ kind: 'control', frame: parsed as HistoryStreamControlFrame });
  }
}
